import apiClient from './client';

/** A Learn chapter list item (feature 021). The UI shows "Chapter {order} - {title}". */
export interface ChapterSummary {
  slug: string;
  title: string;
  order: number;
  /**
   * Whether a PDF of this chapter exists on the server. The reader's Download menu offers the
   * PDF only when true, so a chapter shipped without one never shows an option that 404s.
   * Optional so a response from an older API (or a test fixture) reads as "no PDF".
   */
  hasPdf?: boolean;
}

/** Which file the per-chapter download returns. Matches the API's `kind` query parameter. */
export type ChapterDocumentKind = 'word' | 'pdf';

function parseFilename(contentDisposition?: string): string | null {
  if (!contentDisposition) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(contentDisposition);
  return match && match[1] ? decodeURIComponent(match[1]) : null;
}

export const learnApi = {
  /** The Precision Notes chapter list, ascending by order. */
  getChapters: async (): Promise<ChapterSummary[]> => {
    const response = await apiClient.get<ChapterSummary[]>('/learn/chapters');
    return response.data;
  },

  /** A chapter's HTML fragment (returned as text, not JSON). */
  getChapterContent: async (slug: string): Promise<string> => {
    // 404 (unknown/missing chapter) is handled inline as a "not available" state, so opt out of the
    // axios interceptor's error redirect for this call.
    const response = await apiClient.get<string>(
      `/learn/chapters/${encodeURIComponent(slug)}/content`,
      { responseType: 'text', skipRedirectOn404: true } as never,
    );
    return response.data;
  },

  /**
   * Fetch one chapter's document as a blob plus its download filename (authenticated).
   * Defaults to Word so existing callers keep their behaviour.
   */
  downloadChapterDocument: async (
    slug: string,
    kind: ChapterDocumentKind = 'word',
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await apiClient.get(
      `/learn/chapters/${encodeURIComponent(slug)}/document`,
      { responseType: 'blob', params: { kind } } as never,
    );
    const disposition = (response.headers as Record<string, string>)['content-disposition'];
    const fallbackExt = kind === 'pdf' ? 'pdf' : 'docx';
    return {
      blob: response.data as Blob,
      filename: parseFilename(disposition) ?? `${slug}.${fallbackExt}`,
    };
  },

  /** Feature 022 — the two bulk-download ZIPs (all chapters' Word / PDF documents), authenticated. */
  downloadDocumentsZip: async (
    kind: 'word' | 'pdf',
  ): Promise<{ blob: Blob; filename: string }> => {
    const response = await apiClient.get(`/learn/documents/${kind}.zip`, {
      responseType: 'blob',
    } as never);
    const disposition = (response.headers as Record<string, string>)['content-disposition'];
    return {
      blob: response.data as Blob,
      filename: parseFilename(disposition) ?? `precision-notes-${kind}.zip`,
    };
  },
};

/** Trigger a browser "save as" for a fetched blob (shared by the download buttons). */
export function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
